import {
  Building2,
  DollarSign,
  LogOut,
  LayoutDashboard,
  PanelLeftClose,
  ShieldCheck,
  ChevronRight,
  Gavel,
  Briefcase,
  FolderOpen,
} from "lucide-react";
import { NavLink } from "@/shared/components/NavLink";
import { useNavigate, useLocation } from "react-router-dom";
import logoSidebar from "@/shared/assets/logo-sidebar-ferrugini.png";
import { useFinanceAccess } from "@/shared/hooks/useFinanceAccess";
import { usePermissions } from "@/shared/hooks/usePermissions";
import FeedbackDialog from "@/shared/components/FeedbackDialog";
import {
  Sidebar,
  SidebarContent,
  SidebarGroup,
  SidebarGroupContent,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarFooter,
  useSidebar,
} from "@/shared/components/ui/sidebar";
import type { UserPermissionsData } from "@/shared/components/users/UserPermissions";

const AppSidebar = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { toggleSidebar, state: sidebarState } = useSidebar();
  const isExpanded = sidebarState === "expanded";
  const { canAccessFullFinance } = useFinanceAccess();
  const { hasPermission, qualificacao, loading: permissionsLoading } = usePermissions();

  const allItems: { title: string; url: string; icon: any; end?: boolean; module?: keyof UserPermissionsData; requireFinance?: boolean }[] = [
    { title: "Início", url: "/dashboard", icon: LayoutDashboard, end: true, module: "dashboard" },
    { title: "Gestão Jurídica", url: "/dashboard/juridica", icon: Gavel, module: "gestao_juridica" },
    { title: "Gestão Administrativa", url: "/dashboard/administrativa", icon: Briefcase, module: "gestao_administrativa" },
    { title: "Gestão Financeira", url: "/dashboard/finance-home", icon: DollarSign, module: "financeiro", requireFinance: true },
    { title: "Arquivo Digital", url: "/dashboard/arquivo", icon: FolderOpen },
    { title: "Auditoria e Planejamento", url: "/dashboard/audits", icon: ShieldCheck, module: "auditoria" },
    { title: "Instituições", url: "/dashboard/institutions", icon: Building2, module: "instituicoes" },
  ];

  const mainItems = allItems.filter((item) => {
    if (item.requireFinance && !canAccessFullFinance) return false;
    if (item.module && !permissionsLoading && !hasPermission(item.module, "visualizar")) return false;
    if (item.title === "Arquivo Digital" && qualificacao === "institution_external") return false;
    return true;
  });

  const isActive = (url: string, end?: boolean) => {
    if (end) return location.pathname === url;
    return location.pathname.startsWith(url);
  };

  return (
    <Sidebar className="border-r-0 fg-sidebar-glass border-r border-[#d7b369]/30 backdrop-blur-md">
      {/* Logo Ferrugini Advogados Associados */}
      <div className="px-4 pt-5 pb-4 flex items-start justify-between gap-2">
        <div className="flex min-w-0 flex-1 flex-col gap-2">
          <img
            src={logoSidebar}
            alt="Ferrugini Advogados Associados"
            className="h-16 w-auto max-w-full object-contain object-left drop-shadow-md shrink-0"
            draggable={false}
          />
        </div>
        <button
          onClick={toggleSidebar}
          className="p-1.5 rounded-md text-white/60 hover:text-white hover:bg-[#d7b369]/20 transition-colors shrink-0 mt-1"
          title={isExpanded ? "Recolher menu" : "Expandir menu"}
        >
          <PanelLeftClose className="h-4 w-4" />
        </button>
      </div>

      {/* Gold gradient separator — 1.5px */}
      <div className="mx-5 mb-4">
        <div className="h-[1.5px] bg-gradient-to-r from-transparent via-[#d7b369]/70 to-transparent" />
      </div>

      <SidebarContent className="px-3 pt-0">
        <SidebarGroup className="py-0">
          <SidebarGroupContent>
            <SidebarMenu className="space-y-1">
              {mainItems.map((item) => {
                const active = isActive(item.url, item.end);
                return (
                  <SidebarMenuItem key={item.title}>
                    <SidebarMenuButton
                      asChild
                      className="h-auto min-h-[44px] p-0 overflow-visible [&>span:last-child]:!truncate-none [&>span:last-child]:!whitespace-normal [&>span:last-child]:!overflow-visible"
                    >
                      <NavLink
                        to={item.url}
                        end={item.end}
                        className={`
                          group relative flex items-center gap-3 px-3 py-2.5 rounded-lg
                          transition-all duration-200 font-body
                          ${active
                            ? "bg-[#d7b369]/15 text-white font-medium border-l-[3px] border-[#d7b369] pl-[10px]"
                            : "text-white/65 hover:text-white/90 hover:bg-white/8"
                          }
                        `}
                        activeClassName=""
                      >
                        <item.icon
                          className={`h-[18px] w-[18px] shrink-0 transition-colors ${
                            active
                              ? "text-[#d7b369]"
                              : "text-white/55 group-hover:text-white/90"
                          }`}
                        />
                        <span className="text-[13.5px] font-medium flex-1 leading-snug break-words min-w-0 tracking-[0.01em]">
                          {item.title}
                        </span>
                        {active && (
                          <ChevronRight className="h-3.5 w-3.5 text-[#d7b369] shrink-0" />
                        )}
                      </NavLink>
                    </SidebarMenuButton>
                  </SidebarMenuItem>
                );
              })}
            </SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>
      </SidebarContent>

      {/* Footer */}
      <SidebarFooter className="p-3 pt-2">
        <div className="mx-2 mb-3">
          <div className="h-[1.5px] bg-gradient-to-r from-transparent via-[#d7b369]/40 to-transparent" />
        </div>
        <SidebarMenu className="space-y-0.5">
          <SidebarMenuItem>
            <SidebarMenuButton asChild className="h-10 p-0">
              <FeedbackDialog />
            </SidebarMenuButton>
          </SidebarMenuItem>
          <SidebarMenuItem>
            <SidebarMenuButton
              onClick={() => navigate("/")}
              className="h-10 text-white/60 hover:text-destructive hover:bg-destructive/10 rounded-lg transition-colors font-body"
            >
              <LogOut className="mr-3 h-[18px] w-[18px]" />
              <span className="text-sm">Sair</span>
            </SidebarMenuButton>
          </SidebarMenuItem>
        </SidebarMenu>
      </SidebarFooter>
    </Sidebar>
  );
};

export default AppSidebar;
